import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";
import type { MasterControlIngestionPipeline } from "@/hooks/useMasterControl";

type MCIngestionDetailsDrawerProps = {
  pipeline: MasterControlIngestionPipeline | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRun?: (pipeline: MasterControlIngestionPipeline) => Promise<void>;
  running?: boolean;
  canRun?: boolean;
};

export function MCIngestionDetailsDrawer({
  pipeline,
  open,
  onOpenChange,
  onRun,
  running,
  canRun = true,
}: MCIngestionDetailsDrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-primary-foreground dark:text-amber-200">
                {pipeline?.label ?? "Ingestion pipeline"}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">
                Detailed telemetry and metadata for the selected ingestion job.
              </Dialog.Description>
            </div>
            <button
              type="button"
              className="rounded-full border border-border p-1 text-foreground/80 hover:text-primary-foreground dark:text-amber-200"
              onClick={() => onOpenChange(false)}
            >
              ×
            </button>
          </div>
          {pipeline ? (
            <div className="mt-4 space-y-4 text-sm text-foreground/80">
              <div className="rounded-xl border border-border bg-background/30 p-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Pending</p>
                    <p className="text-lg font-semibold text-foreground">{pipeline.pending}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Last run</p>
                    <p>{pipeline.last_run ? new Date(pipeline.last_run).toLocaleString() : "Unknown"}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                    <p className="capitalize">{pipeline.status}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Last error</p>
                    <p className="text-rose-300">{pipeline.last_error ?? "None"}</p>
                  </div>
                </div>
              </div>
              {pipeline.metadata && (
                <div>
                  <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Metadata</p>
                  <CodeBlock value={JSON.stringify(pipeline.metadata, null, 2)} language="json" className="max-h-72" />
                </div>
              )}
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">Choose an ingestion pipeline to view telemetry.</p>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <Button
              variant="destructive"
              disabled={!onRun || !pipeline || running || !canRun}
              onClick={() => {
                if (onRun && pipeline) {
                  void onRun(pipeline);
                }
              }}
              title={canRun ? undefined : "Upgrade to unlock run access"}
            >
              {running ? "Dispatching…" : "Run pipeline"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default MCIngestionDetailsDrawer;
